import { useEffect, useRef, useState } from "react";
import api from "../api/client.js";
import { useToast } from "../context/ToastContext.jsx";
import { crc, fecha, hoy, METODOS_PAGO } from "../utils/format.js";
import { PageHeader, Modal, Loading, EmptyState } from "../components/UI.jsx";
import { Icon } from "../components/Icons.jsx";
import CabysPicker from "../components/CabysPicker.jsx";
import FilterBar from "../components/FilterBar.jsx";

const CATEGORIAS_GASTO = [
  "Alquiler",
  "Servicios",
  "Planilla",
  "Mantenimiento",
  "Insumos",
  "Transporte",
  "Impuestos",
  "General",
];

function inicioMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function Gastos() {
  const toast = useToast();
  const [gastos, setGastos] = useState(null);
  const [totales, setTotales] = useState(null);
  const [modal, setModal] = useState(false);
  const [modalImport, setModalImport] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({});
  const [desde, setDesde] = useState(inicioMes());
  const [hasta, setHasta] = useState(hoy());
  const [q, setQ] = useState("");
  const [categoria, setCategoria] = useState("");
  const [metodo, setMetodo] = useState("");
  const [conComp, setConComp] = useState("");
  const [ivaAcred, setIvaAcred] = useState("");
  const [importForm, setImportForm] = useState({
    categoria: "General",
    descripcion: "",
    metodo_pago: "transferencia",
    codigo_cabys: "",
  });
  const [importFiles, setImportFiles] = useState({
    xml_comprobante: null,
    xml_respuesta: null,
    pdf: null,
  });
  const [importando, setImportando] = useState(false);
  const [previewImport, setPreviewImport] = useState(null);
  const fileCompRef = useRef(null);
  const fileRespRef = useRef(null);
  const filePdfRef = useRef(null);

  const cargar = () => {
    const params = {};
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    if (q) params.q = q;
    if (categoria) params.categoria = categoria;
    if (metodo) params.metodo_pago = metodo;
    if (conComp) params.con_comprobante = conComp;
    if (ivaAcred) params.iva_acreditable = ivaAcred;
    api.get("/gastos", { params }).then((r) => {
      const d = r.data.data;
      if (Array.isArray(d)) {
        setGastos(d);
        setTotales(null);
      } else {
        setGastos(d.items || []);
        setTotales(d.totales || null);
      }
    });
  };
  useEffect(cargar, [desde, hasta, q, categoria, metodo, conComp, ivaAcred]);

  const limpiar = () => {
    setDesde(inicioMes());
    setHasta(hoy());
    setQ("");
    setCategoria("");
    setMetodo("");
    setConComp("");
    setIvaAcred("");
  };

  const abrirNuevo = () => {
    setEditando(null);
    setForm({
      descripcion: "",
      categoria: "General",
      monto: "",
      fecha: hoy(),
      metodo_pago: "efectivo",
      codigo_cabys: "",
    });
    setModal(true);
  };
  const abrirEditar = (g) => {
    setEditando(g.id);
    setForm({ ...g, fecha: g.fecha?.slice(0, 10) });
    setModal(true);
  };

  const abrirImport = () => {
    setImportForm({
      categoria: "General",
      descripcion: "",
      metodo_pago: "transferencia",
      codigo_cabys: "",
    });
    setImportFiles({ xml_comprobante: null, xml_respuesta: null, pdf: null });
    setPreviewImport(null);
    setModalImport(true);
  };

  const guardar = async () => {
    if (!form.descripcion || !form.monto) {
      toast.error("Descripcion y monto requeridos");
      return;
    }
    try {
      if (editando) await api.put(`/gastos/${editando}`, form);
      else await api.post("/gastos", form);
      toast.success("Gasto guardado");
      setModal(false);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al guardar");
    }
  };

  const importar = async () => {
    if (!importFiles.xml_comprobante) {
      toast.error("Suba el XML del comprobante electronico del proveedor");
      return;
    }
    setImportando(true);
    setPreviewImport(null);
    try {
      const fd = new FormData();
      fd.append("xml_comprobante", importFiles.xml_comprobante);
      if (importFiles.xml_respuesta) fd.append("xml_respuesta", importFiles.xml_respuesta);
      if (importFiles.pdf) fd.append("pdf", importFiles.pdf);
      fd.append("categoria", importForm.categoria || "General");
      fd.append("metodo_pago", importForm.metodo_pago || "transferencia");
      if (importForm.descripcion) fd.append("descripcion", importForm.descripcion);
      if (importForm.codigo_cabys) fd.append("codigo_cabys", importForm.codigo_cabys);

      const { data } = await api.post("/gastos/importar-electronico", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const r = data.data;
      setPreviewImport(r);
      if (r.aviso) toast.error(r.aviso);
      else toast.success("Gasto electronico importado");
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo importar el gasto");
    } finally {
      setImportando(false);
    }
  };

  const eliminar = async (g) => {
    if (!confirm("Eliminar este gasto?")) return;
    await api.delete(`/gastos/${g.id}`);
    toast.success("Eliminado");
    cargar();
  };

  const resumen = totales
    ? `${totales.cantidad} gasto(s) · Total ${crc(totales.total)} · IVA ${crc(totales.iva)}`
    : gastos
      ? `${gastos.length} gasto(s)`
      : "";

  return (
    <div>
      <PageHeader title="Gastos" subtitle="Historico de egresos y gastos electronicos">
        <button className="btn btn-outline" onClick={abrirImport}>
          <Icon.plus /> Importar XML
        </button>
        <button className="btn btn-primary" onClick={abrirNuevo}>
          <Icon.plus /> Nuevo gasto
        </button>
      </PageHeader>

      <FilterBar
        desde={desde}
        hasta={hasta}
        onDesde={setDesde}
        onHasta={setHasta}
        busqueda={q}
        onBusqueda={setQ}
        showBusqueda
        placeholderBusqueda="Descripcion, clave, proveedor…"
        onLimpiar={limpiar}
        resumen={resumen}
      >
        <div className="field" style={{ margin: 0, minWidth: 130 }}>
          <label>Categoria</label>
          <select
            className="select"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
          >
            <option value="">Todas</option>
            {CATEGORIAS_GASTO.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 130 }}>
          <label>Pago</label>
          <select className="select" value={metodo} onChange={(e) => setMetodo(e.target.value)}>
            <option value="">Todos</option>
            {METODOS_PAGO.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 140 }}>
          <label>Comprobante</label>
          <select className="select" value={conComp} onChange={(e) => setConComp(e.target.value)}>
            <option value="">Todos</option>
            <option value="1">Con XML</option>
            <option value="0">Sin XML</option>
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 140 }}>
          <label>IVA acreditable</label>
          <select className="select" value={ivaAcred} onChange={(e) => setIvaAcred(e.target.value)}>
            <option value="">Todos</option>
            <option value="1">Si</option>
            <option value="0">No</option>
          </select>
        </div>
      </FilterBar>

      {totales && (
        <div
          className="card"
          style={{
            padding: 18,
            marginBottom: 16,
            display: "inline-flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <span className="muted" style={{ fontSize: 13 }}>
            Total del periodo
          </span>
          <span
            style={{
              fontSize: 24,
              fontWeight: 700,
              fontFamily: "var(--font-display)",
              color: "var(--danger)",
            }}
          >
            {crc(totales.total)}
          </span>
        </div>
      )}

      {!gastos ? (
        <Loading />
      ) : gastos.length === 0 ? (
        <EmptyState
          icon="gastos"
          titulo="Sin gastos"
          texto="Registra un gasto manual o importa el XML electronico del proveedor."
        />
      ) : (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Descripcion</th>
                <th>Categoria</th>
                <th>Pago</th>
                <th>Respaldo</th>
                <th className="text-right">IVA acred.</th>
                <th className="text-right">Monto</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {gastos.map((g) => (
                <tr key={g.id}>
                  <td className="muted">{fecha(g.fecha)}</td>
                  <td style={{ fontWeight: 600 }}>
                    <div>{g.descripcion}</div>
                    {g.clave_comprobante && (
                      <div className="mono muted" style={{ fontSize: 11 }}>
                        {g.clave_comprobante}
                      </div>
                    )}
                  </td>
                  <td>
                    <span
                      className="badge"
                      style={{
                        background: "var(--accent-soft)",
                        color: "var(--accent-texto)",
                      }}
                    >
                      {g.categoria}
                    </span>
                  </td>
                  <td className="muted" style={{ textTransform: "capitalize" }}>
                    {g.metodo_pago}
                  </td>
                  <td>
                    {g.comprobante_recibido_id || g.clave_comprobante ? (
                      <span
                        style={{
                          color: Number(g.iva_acreditable)
                            ? "var(--success, #16a34a)"
                            : "var(--warning, #d97706)",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                        title={g.clave_comprobante || ""}
                      >
                        {Number(g.iva_acreditable)
                          ? "XML aceptado"
                          : "XML sin acreditar"}
                      </span>
                    ) : (
                      <span className="muted" style={{ fontSize: 13 }}>
                        Sin comprobante
                      </span>
                    )}
                  </td>
                  <td className="text-right mono">
                    {Number(g.iva_monto) > 0 ? (
                      crc(g.iva_monto)
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="text-right mono" style={{ fontWeight: 600 }}>
                    {crc(g.monto)}
                  </td>
                  <td>
                    <div
                      className="flex gap-sm"
                      style={{ justifyContent: "flex-end" }}
                    >
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => abrirEditar(g)}
                      >
                        <Icon.edit width={15} height={15} />
                      </button>
                      <button
                        className="btn btn-sm"
                        style={{ color: "var(--danger)" }}
                        onClick={() => eliminar(g)}
                      >
                        <Icon.trash width={15} height={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- Manual ---- */}
      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editando ? "Editar gasto" : "Nuevo gasto"}
      >
        <div className="field">
          <label>Descripcion *</label>
          <input
            className="input"
            value={form.descripcion || ""}
            onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
          />
        </div>
        <div
          className="grid-2" style={{ gap: 12 }}
        >
          <div className="field">
            <label>Categoria</label>
            <select
              className="select"
              value={form.categoria || "General"}
              onChange={(e) => setForm({ ...form, categoria: e.target.value })}
            >
              {CATEGORIAS_GASTO.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Monto (₡) *</label>
            <input
              className="input"
              type="number"
              value={form.monto || ""}
              onChange={(e) => setForm({ ...form, monto: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Fecha</label>
            <input
              className="input"
              type="date"
              value={form.fecha || ""}
              onChange={(e) => setForm({ ...form, fecha: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Metodo de pago</label>
            <select
              className="select"
              value={form.metodo_pago || "efectivo"}
              onChange={(e) =>
                setForm({ ...form, metodo_pago: e.target.value })
              }
            >
              {METODOS_PAGO.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <CabysPicker
            value={form.codigo_cabys || ""}
            onChange={(codigo) => setForm({ ...form, codigo_cabys: codigo })}
            label="CAByS (opcional, respaldo fiscal)"
          />
        </div>
        <div
          className="flex gap-sm"
          style={{ justifyContent: "flex-end", marginTop: 8 }}
        >
          <button className="btn btn-outline" onClick={() => setModal(false)}>
            Cancelar
          </button>
          <button className="btn btn-primary" onClick={guardar}>
            Guardar
          </button>
        </div>
      </Modal>

      {/* ---- Importar electronico ---- */}
      <Modal
        open={modalImport}
        onClose={() => setModalImport(false)}
        title="Importar gasto electronico"
        width={560}
      >
        <p className="muted" style={{ marginBottom: 14, fontSize: 13 }}>
          Suba el <strong>XML del comprobante</strong> que le envio el proveedor
          y, si ya lo tiene, el <strong>XML de respuesta</strong> (Mensaje
          Receptor o Mensaje de Hacienda). El PDF es opcional (solo se guarda el
          nombre como referencia).
        </p>

        <FileSlot
          label="XML del comprobante *"
          hint="FacturaElectronica / Nota / etc."
          accept=".xml,text/xml,application/xml"
          file={importFiles.xml_comprobante}
          inputRef={fileCompRef}
          onPick={(f) =>
            setImportFiles((s) => ({ ...s, xml_comprobante: f || null }))
          }
        />
        <FileSlot
          label="XML de respuesta"
          hint="MensajeReceptor o MensajeHacienda (para acreditar IVA)"
          accept=".xml,text/xml,application/xml"
          file={importFiles.xml_respuesta}
          inputRef={fileRespRef}
          onPick={(f) =>
            setImportFiles((s) => ({ ...s, xml_respuesta: f || null }))
          }
        />
        <FileSlot
          label="PDF (opcional)"
          hint="Representacion visual del comprobante"
          accept=".pdf,application/pdf"
          file={importFiles.pdf}
          inputRef={filePdfRef}
          onPick={(f) => setImportFiles((s) => ({ ...s, pdf: f || null }))}
        />

        <div
          className="grid-2" style={{ gap: 12, marginTop: 8 }}
        >
          <div className="field">
            <label>Categoria</label>
            <select
              className="select"
              value={importForm.categoria}
              onChange={(e) =>
                setImportForm({ ...importForm, categoria: e.target.value })
              }
            >
              {CATEGORIAS_GASTO.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Metodo de pago</label>
            <select
              className="select"
              value={importForm.metodo_pago}
              onChange={(e) =>
                setImportForm({ ...importForm, metodo_pago: e.target.value })
              }
            >
              {METODOS_PAGO.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label>Descripcion (opcional)</label>
          <input
            className="input"
            placeholder="Se arma sola con emisor + consecutivo si la deja vacia"
            value={importForm.descripcion}
            onChange={(e) =>
              setImportForm({ ...importForm, descripcion: e.target.value })
            }
          />
        </div>

        {previewImport && (
          <div
            className="card"
            style={{
              padding: 12,
              marginTop: 10,
              background: "var(--accent-soft)",
              border: "1px solid var(--border)",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Importado</div>
            <div style={{ fontSize: 13 }}>
              <div>
                <strong>Emisor:</strong> {previewImport.emisor}
              </div>
              <div className="mono">
                <strong>Clave:</strong> {previewImport.clave}
              </div>
              <div>
                <strong>Total:</strong> {crc(previewImport.total)} ·{" "}
                <strong>IVA:</strong> {crc(previewImport.iva_monto)}{" "}
                {previewImport.iva_acreditable ? "(acreditable)" : "(no acreditable)"}
              </div>
              <div>
                <strong>Estado fiscal:</strong> {previewImport.estado_fiscal}
              </div>
            </div>
          </div>
        )}

        <div
          className="flex gap-sm"
          style={{ justifyContent: "flex-end", marginTop: 14 }}
        >
          <button
            className="btn btn-outline"
            onClick={() => setModalImport(false)}
            disabled={importando}
          >
            {previewImport ? "Cerrar" : "Cancelar"}
          </button>
          {!previewImport && (
            <button
              className="btn btn-primary"
              onClick={importar}
              disabled={importando || !importFiles.xml_comprobante}
            >
              {importando ? <span className="spinner" /> : "Importar gasto"}
            </button>
          )}
          {previewImport && (
            <button
              className="btn btn-primary"
              onClick={() => {
                setPreviewImport(null);
                setImportFiles({
                  xml_comprobante: null,
                  xml_respuesta: null,
                  pdf: null,
                });
              }}
            >
              Importar otro
            </button>
          )}
        </div>
      </Modal>
    </div>
  );
}

function FileSlot({ label, hint, accept, file, inputRef, onPick }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 10,
        border: "1px dashed var(--border)",
        marginBottom: 10,
        background: "var(--surface-2, transparent)",
      }}
    >
      <div className="flex items-center justify-between" style={{ gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
          <div className="muted" style={{ fontSize: 12 }}>
            {file ? file.name : hint}
          </div>
        </div>
        <div className="flex gap-sm">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            style={{ display: "none" }}
            onChange={(e) => onPick(e.target.files?.[0] || null)}
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => inputRef.current?.click()}
          >
            {file ? "Cambiar" : "Elegir archivo"}
          </button>
          {file && (
            <button
              type="button"
              className="btn btn-sm"
              style={{ color: "var(--danger)" }}
              onClick={() => {
                onPick(null);
                if (inputRef.current) inputRef.current.value = "";
              }}
            >
              Quitar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
